import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from 'posthog-js/react';
import { FlexCenHorVer } from '@/UI/styles';
import { MenuAction } from '@/components/Menu/Actions';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { I18N_KEYS } from '@/language/keys';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { SubscriptionType } from '@/types/shared';
import {
  FF_DISABLE_SHARE_FOR_FREE_USERS,
  FF_DISABLE_SHARE_FOR_FREE_USERS_VARIANT_TEST,
  FF_KEY_NAVIGATION,
} from '@/consts/featureFlags';
import { useWorkflowStore } from '@/state/workflow.state';
import { color } from '@/colors';
import { useGlobalStore } from '@/state/global.state';
import { getOS } from '@/utils/general';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { useCreateRecipe } from '@/queries/useRecipes';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { OpenRecentMenuItem } from './OpenRecentMenuItem';
import { EdgeTypeMenu } from './EdgeTypeMenu';
import type { EdgeTypePreference } from '@/types/auth.types';

interface WorkflowFileMenuProps {
  isMenuOpen: boolean;
  goToDashboard: () => void;
  onDuplicateRecipe: (recipeId: string) => void;
  panOnScrollEnabled?: boolean;
  onPanOnScrollChange?: (enabled: boolean) => void;
  showFloatMenuOnRightClickEnabled?: boolean;
  onShowFloatMenuOnRightClickChange?: (enabled: boolean) => void;
  requireAltKeyForSuggestionsEnabled?: boolean;
  onRequireAltKeyForSuggestionsChange?: (enabled: boolean) => void;
  snapToGridEnabled?: boolean;
  onSnapToGridChange?: (enabled: boolean) => void;
  edgeType?: EdgeTypePreference;
  onEdgeTypeChange?: (value: EdgeTypePreference) => void;
  onShareWorkflow: () => void;
}

function WorkflowFileMenu({
  isMenuOpen,
  goToDashboard,
  onDuplicateRecipe,
  panOnScrollEnabled,
  onPanOnScrollChange,
  showFloatMenuOnRightClickEnabled,
  onShowFloatMenuOnRightClickChange,
  requireAltKeyForSuggestionsEnabled,
  onRequireAltKeyForSuggestionsChange,
  snapToGridEnabled,
  onSnapToGridChange,
  edgeType,
  onEdgeTypeChange,
  onShareWorkflow,
}: WorkflowFileMenuProps) {
  const [anchorElUserMenu, setAnchorElUserMenu] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const disableShareExperimentVariant = useFeatureFlagVariantKey(FF_DISABLE_SHARE_FOR_FREE_USERS);
  const isSharingDisabled =
    disableShareExperimentVariant === FF_DISABLE_SHARE_FOR_FREE_USERS_VARIANT_TEST &&
    isSubscriptionTypeIn([SubscriptionType.Free]);
  const currentRecipeId = useWorkflowStore((state) => state.recipe.id);
  const { mutate: createRecipe } = useCreateRecipe();
  const setIsEditingNameOnToolbar = useWorkflowStore((state) => state.setIsEditingNameOnToolbar);
  const setIsEditingNameOnWorkflow = useWorkflowStore((state) => state.setIsEditingNameOnWorkflow);

  const ff_isKeyNavigationEnabled = useFeatureFlagEnabled(FF_KEY_NAVIGATION);
  const setIsShortcutsPanelOpen = useGlobalStore((s) => s.setIsShortcutsPanelOpen);
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElUserMenu(event.currentTarget);
  const isFileMenuOpen = Boolean(anchorElUserMenu);

  const getKeyNameByOs = useCallback(() => {
    const keyNameByOs = {
      Mac: 'Option',
      Windows: 'Alt',
      Linux: 'Option',
      Other: 'Option',
    };
    return keyNameByOs[getOS() ?? 'Other'];
  }, []);

  const handleCloseUserMenu = useCallback(() => setAnchorElUserMenu(null), []);

  const handleLogoClick = (event: React.MouseEvent<HTMLElement>) => {
    const isCtrlOrCmd = event.metaKey || event.ctrlKey;

    if (isCtrlOrCmd) {
      // Open dashboard in new tab
      event.preventDefault();
      event.stopPropagation();
      window.open('/', '_blank');
    } else {
      // Normal behavior - open menu
      handleOpenUserMenu(event);
    }
  };

  const handlePanOnScrollToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onPanOnScrollChange && panOnScrollEnabled !== undefined) {
        onPanOnScrollChange(!panOnScrollEnabled);
      }
    },
    [onPanOnScrollChange, panOnScrollEnabled],
  );

  const handleShowFloatMenuOnRightClickToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onShowFloatMenuOnRightClickChange && showFloatMenuOnRightClickEnabled !== undefined) {
        onShowFloatMenuOnRightClickChange(!showFloatMenuOnRightClickEnabled);
      }
    },
    [onShowFloatMenuOnRightClickChange, showFloatMenuOnRightClickEnabled],
  );

  const handleRequireAltKeyForSuggestionsToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onRequireAltKeyForSuggestionsChange && requireAltKeyForSuggestionsEnabled !== undefined) {
        onRequireAltKeyForSuggestionsChange(!requireAltKeyForSuggestionsEnabled);
      }
    },
    [onRequireAltKeyForSuggestionsChange, requireAltKeyForSuggestionsEnabled],
  );

  const handleSnapToGridToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onSnapToGridChange && snapToGridEnabled !== undefined) {
        onSnapToGridChange(!snapToGridEnabled);
      }
    },
    [onSnapToGridChange, snapToGridEnabled],
  );

  const menuItems: MenuAction[] = useMemo(() => {
    const items: MenuAction[] = [
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.BACK_TO_DASHBOARD),
        action: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          goToDashboard();
        },
      },
      {
        type: 'divider',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.CREATE_NEW_FILE),
        action: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          void createRecipe({ openInNewTab: true });
        },
      },
      {
        type: 'slot',
        el: <OpenRecentMenuItem currentRecipeId={currentRecipeId} onClose={handleCloseUserMenu} />,
      },
      {
        type: 'divider',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.DUPLICATE_FILE),
        action: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          onDuplicateRecipe(currentRecipeId);
        },
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.RENAME_WORKFLOW),
        action: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          handleCloseUserMenu();
          if (isMenuOpen) {
            setIsEditingNameOnToolbar(true);
          } else {
            setIsEditingNameOnWorkflow(true);
          }
        },
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.SHARE_WORKFLOW),
        action: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          onShareWorkflow();
        },
        disabled: isSharingDisabled,
      },
      {
        type: 'divider',
      },
      ...(ff_isKeyNavigationEnabled
        ? [
            {
              name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.KEYBOARD_SHORTCUTS),
              action: (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                setIsShortcutsPanelOpen(true);
              },
            },
          ]
        : []),
      {
        type: 'slot',
        el: (
          <ParentMenuItem
            id="preferences"
            label={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.PREFERENCES)}
            onClose={handleCloseUserMenu}
            contextMenuWidth="260px"
            offset={{ left: '10px', top: '-9px' }}
          >
            <AppMenuItem onClick={handlePanOnScrollToggle}>
              <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography fontSize="inherit">
                  {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.USE_WHEEL_TO_ZOOM)}
                </Typography>
                {!panOnScrollEnabled && <CheckMarkIcon />}
              </FlexCenHorVer>
            </AppMenuItem>
            <AppMenuItem onClick={handleShowFloatMenuOnRightClickToggle}>
              <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography fontSize="inherit">
                  {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.RIGHT_CLICK_TO_OPEN_MENU)}
                </Typography>
                {showFloatMenuOnRightClickEnabled && <CheckMarkIcon />}
              </FlexCenHorVer>
            </AppMenuItem>
            <AppMenuItem onClick={handleRequireAltKeyForSuggestionsToggle}>
              <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography fontSize="inherit">
                  {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.REQUIRE_ALT_KEY_FOR_SUGGESTIONS, {
                    key: `${getKeyNameByOs()}`,
                  })}
                </Typography>
                {requireAltKeyForSuggestionsEnabled && <CheckMarkIcon />}
              </FlexCenHorVer>
            </AppMenuItem>
            {snapToGridEnabled !== undefined && onSnapToGridChange && (
              <AppMenuItem onClick={handleSnapToGridToggle}>
                <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                  <Typography fontSize="inherit">
                    {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.SNAP_TO_GRID)}
                  </Typography>
                  {snapToGridEnabled && <CheckMarkIcon />}
                </FlexCenHorVer>
              </AppMenuItem>
            )}
            {edgeType !== undefined && onEdgeTypeChange && (
              <EdgeTypeMenu edgeType={edgeType} onEdgeTypeChange={onEdgeTypeChange} onClose={handleCloseUserMenu} />
            )}
          </ParentMenuItem>
        ),
      },
    ];

    return items;
  }, [
    t,
    ff_isKeyNavigationEnabled,
    goToDashboard,
    createRecipe,
    onDuplicateRecipe,
    currentRecipeId,
    onShareWorkflow,
    setIsShortcutsPanelOpen,
    isMenuOpen,
    setIsEditingNameOnToolbar,
    setIsEditingNameOnWorkflow,
    handlePanOnScrollToggle,
    handleShowFloatMenuOnRightClickToggle,
    handleRequireAltKeyForSuggestionsToggle,
    handleSnapToGridToggle,
    handleCloseUserMenu,
    getKeyNameByOs,
    panOnScrollEnabled,
    requireAltKeyForSuggestionsEnabled,
    showFloatMenuOnRightClickEnabled,
    snapToGridEnabled,
    onSnapToGridChange,
    edgeType,
    onEdgeTypeChange,
    isSharingDisabled,
  ]);

  return (
    <>
      <AppIconButton
        sx={{ mb: 4, backgroundColor: isFileMenuOpen ? color.Black84 : undefined }}
        width={36}
        height={36}
        onClick={handleLogoClick}
      >
        <img src="/icons/logo.svg" alt="Logo" width={24} />
      </AppIconButton>
      <AppContextMenu
        offset={{ top: '4px', left: '-6px' }}
        open={isFileMenuOpen}
        onClose={handleCloseUserMenu}
        items={menuItems}
        width="180px"
        anchorEl={anchorElUserMenu}
        keepMounted
      />
    </>
  );
}

export default WorkflowFileMenu;
